import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { MdCalendarToday } from 'react-icons/md';

/* ── Trigger ─────────────────────────────────────────────── */
const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 11px 14px;
  border: 1px solid ${({ $open }) => ($open ? '#2563eb' : '#cbd5f5')};
  border-radius: 10px;
  background: #ffffff;
  font-size: 14px;
  color: ${({ $hasValue }) => ($hasValue ? '#0f172a' : '#94a3b8')};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.2s;
  box-shadow: ${({ $open }) => ($open ? '0 0 0 3px rgba(37,99,235,0.15)' : 'none')};

  &:hover {
    border-color: #2563eb;
  }

  svg {
    color: #94a3b8;
    flex-shrink: 0;
  }
`;

const TriggerText = styled.span`
  flex: 1;
`;

const ClearBtn = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #e2e8f0;
  color: #475569;
  font-size: 12px;
  line-height: 18px;
  text-align: center;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: #cbd5e1;
  }
`;

/* ── Dropdown ─────────────────────────────────────────────── */
const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 1500;
  width: 280px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.18);
  overflow: hidden;
`;

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #f8fafc;
`;

const NavBtn = styled.button`
  border: none;
  background: transparent;
  color: #475569;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 6px;
  line-height: 1;

  &:hover {
    background: #e2e8f0;
    color: #1d4ed8;
  }
`;

const RangeLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
  padding: 12px;
`;

const YearCell = styled.button`
  border: none;
  border-radius: 8px;
  padding: 10px 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;

  ${({ $selected }) =>
    $selected
      ? `background: #2563eb; color: #ffffff;`
      : `background: transparent; color: #0f172a;`}

  ${({ $current, $selected }) =>
    $current && !$selected
      ? `border: 1.5px solid #2563eb; color: #2563eb;`
      : `border: 1.5px solid transparent;`}

  &:hover {
    background: ${({ $selected }) => ($selected ? '#1d4ed8' : '#e0e7ff')};
    color: ${({ $selected }) => ($selected ? '#ffffff' : '#1d4ed8')};
  }
`;

const TodayRow = styled.div`
  padding: 8px 12px;
  border-top: 1px solid #e2e8f0;
  text-align: center;
`;

const TodayBtn = styled.button`
  border: none;
  background: transparent;
  color: #2563eb;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 6px;

  &:hover {
    background: #eff6ff;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
`;

const PAGE = 9; // years per page

export default function YearPicker({ value, onChange, placeholder = 'Select year' }) {
  const currentYear = new Date().getFullYear();
  const [open, setOpen] = useState(false);
  // anchor is the first year shown on current page
  const [anchor, setAnchor] = useState(() => {
    const base = value ? Number(value) : currentYear;
    return base - ((base % PAGE) || PAGE);
  });

  const ref = useRef(null);

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const years = Array.from({ length: PAGE }, (_, i) => anchor + i);
  const rangeLabel = `${years[0]} – ${years[years.length - 1]}`;

  const selectYear = (yr) => {
    onChange(String(yr));
    setOpen(false);
  };

  const goToday = () => {
    const base = currentYear;
    setAnchor(base - ((base % PAGE) || PAGE));
    onChange(String(currentYear));
    setOpen(false);
  };

  const clear = (e) => {
    e.stopPropagation();
    onChange(null);
  };

  return (
    <Wrapper ref={ref}>
      <Trigger
        type="button"
        $open={open}
        $hasValue={Boolean(value)}
        onClick={() => setOpen((v) => !v)}
      >
        <MdCalendarToday size={16} />
        <TriggerText>{value || placeholder}</TriggerText>
        {value && <ClearBtn onClick={clear}>×</ClearBtn>}
      </Trigger>

      {open && (
        <Dropdown>
          <NavRow>
            <div style={{ display: 'flex', gap: 2 }}>
              <NavBtn type="button" title="Back 27 years" onClick={() => setAnchor((a) => a - PAGE * 3)}>«</NavBtn>
              <NavBtn type="button" title="Back 9 years" onClick={() => setAnchor((a) => a - PAGE)}>‹</NavBtn>
            </div>
            <RangeLabel>{rangeLabel}</RangeLabel>
            <div style={{ display: 'flex', gap: 2 }}>
              <NavBtn type="button" title="Forward 9 years" onClick={() => setAnchor((a) => a + PAGE)}>›</NavBtn>
              <NavBtn type="button" title="Forward 27 years" onClick={() => setAnchor((a) => a + PAGE * 3)}>»</NavBtn>
            </div>
          </NavRow>

          <Grid>
            {years.map((yr) => (
              <YearCell
                key={yr}
                type="button"
                $selected={value && Number(value) === yr}
                $current={yr === currentYear}
                onClick={() => selectYear(yr)}
              >
                {yr}
              </YearCell>
            ))}
          </Grid>

          <TodayRow>
            <TodayBtn type="button" onClick={goToday}>Today</TodayBtn>
          </TodayRow>
        </Dropdown>
      )}
    </Wrapper>
  );
}
